// What each person shows on Home, and the place for the weather. Nothing is ever deleted: a card someone doesn't want is
// hidden (Settings → Home) and can come back. Kept on this device and synced to VERA's server with the person's other
// settings (history.ts), so the same Home follows them to any computer or phone.

import { useCallback, useEffect, useState } from "react";
import { settingsChanged } from "./history";

export type HomeCard = "weather" | "notifications" | "three" | "noticed" | "calendar" | "inbox" | "teams" | "online" | "operations" | "open" | "changes" | "records";

/** Every Home card, in the order Home shows them, with the words Settings uses. */
export const HOME_CARDS: { id: HomeCard; label: string; about: string }[] = [
  { id: "weather", label: "Weather and compass", about: "Next to the time at the top: the weather now and where the wind comes from" },
  { id: "notifications", label: "Notifications", about: "Quick catches: what needs you now" },
  { id: "three", label: "Your three things", about: "The three things that matter most today" },
  { id: "noticed", label: "What VERA noticed", about: "What VERA spotted in your day" },
  { id: "calendar", label: "Calendar", about: "Today's meetings and appointments" },
  { id: "inbox", label: "Inbox", about: "The latest emails" },
  { id: "teams", label: "Teams", about: "Teams chats, or proposal deadlines" },
  { id: "online", label: "Who's online", about: "Who on the team has VERA Studio open (founders and executives)" },
  { id: "operations", label: "Operations overview", about: "Your operations in numbers, counted live from your records" },
  { id: "open", label: "Open work", about: "A brief report of what is still open" },
  { id: "changes", label: "Latest changes", about: "The last few changes by you, your team or VERA" },
  { id: "records", label: "Your records", about: "Clients, boats, jobs, materials, orders: one tap each" },
];

/** Where the weather is for: a place found on the map (the home port), or this device when Location is on. */
export type HomePlace = { name: string; lat: number; lon: number };
export type HomePrefs = { hidden: HomeCard[]; place?: HomePlace | null };

let userId = "";
const key = () => `vera-home-${userId}`;
const EVENT = "vera-home-prefs";

export function setHomeUser(id: string) { userId = id; }

export function readHomePrefs(): HomePrefs {
  try {
    const raw = localStorage.getItem(key());
    const v = raw ? (JSON.parse(raw) as Partial<HomePrefs>) : {};
    return { hidden: Array.isArray(v.hidden) ? v.hidden.filter((h): h is HomeCard => HOME_CARDS.some((c) => c.id === h)) : [], place: v.place ?? null };
  } catch { return { hidden: [] }; }
}

export function saveHomePrefs(p: HomePrefs) {
  try { localStorage.setItem(key(), JSON.stringify(p)); } catch { /* private window: this visit only */ }
  settingsChanged();
  window.dispatchEvent(new Event(EVENT));
}

/** The person's Home choices, kept up to date when Settings changes them. */
export function useHomePrefs() {
  const [prefs, setPrefs] = useState<HomePrefs>(readHomePrefs);
  useEffect(() => {
    const on = () => setPrefs(readHomePrefs());
    window.addEventListener(EVENT, on);
    window.addEventListener("storage", on);
    return () => { window.removeEventListener(EVENT, on); window.removeEventListener("storage", on); };
  }, []);
  const shows = useCallback((c: HomeCard) => !prefs.hidden.includes(c), [prefs]);
  const toggle = useCallback((c: HomeCard, show: boolean) => {
    const now = readHomePrefs();
    saveHomePrefs({ ...now, hidden: show ? now.hidden.filter((h) => h !== c) : [...new Set([...now.hidden, c])] });
  }, []);
  const setPlace = useCallback((place: HomePlace | null) => saveHomePrefs({ ...readHomePrefs(), place }), []);
  return { prefs, shows, toggle, setPlace };
}
